import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Link } from "react-router-dom";
import { AnimatedContainer } from "@jambushrushti/components/ui/animated-container";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  GraduationCap,
  Users,
  BookOpen,
  Globe,
  ArrowRight,
  Mail,
  MapPin,
  FileText,
  Award,
  UserCheck,
  Quote,
  Scroll,
  Search,
  Building2,
  BookMarked,
  Library,
  Languages,
  History,
  Sparkles,
  Star,
  TrendingUp,
  ExternalLink,
  LogIn,
} from "lucide-react";

// Import scholar profile images
import scholarMale1 from "@jambushrushti/assets/scholars/scholar-male-1.jpg";
import scholarFemale1 from "@jambushrushti/assets/scholars/scholar-female-1.jpg";
import scholarMale2 from "@jambushrushti/assets/scholars/scholar-male-2.jpg";
import scholarMale3 from "@jambushrushti/assets/scholars/scholar-male-3.jpg";
import scholarFemale2 from "@jambushrushti/assets/scholars/scholar-female-2.jpg";
import scholarMale4 from "@jambushrushti/assets/scholars/scholar-male-4.jpg";

const scholars = [
  {
    id: 1,
    name: "Dr. Prabhakaran Jain",
    affiliation: "University of Mumbai",
    specialization: ["Agama Studies", "Prakrit Literature"],
    location: "Mumbai, India",
    country: "India",
    contributions: 45,
    publications: 28,
    citations: 156,
    manuscripts: 12,
    badge: "gold",
    avatar: scholarMale1,
  },
  {
    id: 2,
    name: "Prof. Sadhvi M.S.",
    affiliation: "Jain Research Institute",
    specialization: ["Philosophy", "Ethics"],
    location: "Ahmedabad, India",
    country: "India",
    contributions: 78,
    publications: 42,
    citations: 234,
    manuscripts: 18,
    badge: "gold",
    avatar: scholarFemale1,
  },
  {
    id: 3,
    name: "Dr. Robert Zydenbos",
    affiliation: "Ludwig Maximilian University",
    specialization: ["Comparative Religion", "Jain Studies"],
    location: "Munich, Germany",
    country: "Germany",
    contributions: 32,
    publications: 19,
    citations: 87,
    manuscripts: 5,
    badge: "blue",
    avatar: scholarMale2,
  },
  {
    id: 4,
    name: "Dr. Jayanti Lal Jain",
    affiliation: "BHU Varanasi",
    specialization: ["Manuscript Studies", "Sanskrit"],
    location: "Varanasi, India",
    country: "India",
    contributions: 56,
    publications: 31,
    citations: 178,
    manuscripts: 24,
    badge: "gold",
    avatar: scholarMale3,
  },
  {
    id: 5,
    name: "Dr. Ananya Sharma",
    affiliation: "SOAS University of London",
    specialization: ["Jain Philosophy", "Linguistics"],
    location: "London, UK",
    country: "UK",
    contributions: 23,
    publications: 15,
    citations: 62,
    manuscripts: 3,
    badge: "blue",
    avatar: scholarFemale2,
  },
  {
    id: 6,
    name: "Dr. Mahesh Patel",
    affiliation: "Gujarat University",
    specialization: ["History", "Manuscriptology"],
    location: "Ahmedabad, India",
    country: "India",
    contributions: 12,
    publications: 8,
    citations: 24,
    manuscripts: 6,
    badge: "gray",
    avatar: scholarMale4,
  },
];

const features = [
  {
    icon: BookOpen,
    title: "Research Access",
    description:
      "Access comprehensive databases, rare manuscripts, and advanced research tools designed for Jain scholarship.",
    href: "/research",
    cta: "Explore →",
  },
  {
    icon: Users,
    title: "Collaboration",
    description:
      "Connect with fellow scholars worldwide, share insights, and collaborate on groundbreaking research projects.",
    href: "/scholars",
    cta: "Connect →",
  },
  {
    icon: FileText,
    title: "Publications",
    description:
      "Submit and publish your research papers, articles, and findings to reach a global academic audience.",
    href: "/articles",
    cta: "Publish →",
  },
  {
    icon: UserCheck,
    title: "Scholar Profile",
    description:
      "Build your verified researcher identity, showcase your work, and establish credibility in Jain studies.",
    href: "/auth",
    cta: "Get Verified →",
  },
];

const highlights = [
  {
    icon: FileText,
    title: "Publications",
    value: "250+",
    description: "Research papers, studies & articles",
  },
  {
    icon: Building2,
    title: "Collaborations",
    value: "45+",
    description: "Global university partnerships",
  },
  {
    icon: Scroll,
    title: "Research Domains",
    value: "12",
    description: "Philosophy, Agama, Prakrit, Manuscripts",
  },
  {
    icon: Globe,
    title: "Countries",
    value: "18",
    description: "Scholars from around the world",
  },
];

const benefits = [
  {
    icon: Scroll,
    title: "Access Rare Manuscripts",
    description:
      "Get exclusive access to rare manuscripts and research tools from our comprehensive database.",
  },
  {
    icon: Globe,
    title: "Global Collaboration",
    description:
      "Connect and collaborate with leading scholars and researchers from around the world.",
  },
  {
    icon: Award,
    title: "Get Featured & Published",
    description:
      "Showcase your research and get your work featured in our scholarly publications.",
  },
];

const researchDomains = [
  { icon: BookMarked, name: "Agama Studies", count: 45 },
  { icon: Sparkles, name: "Jain Philosophy", count: 38 },
  { icon: Languages, name: "Prakrit & Sanskrit", count: 32 },
  { icon: Scroll, name: "Manuscriptology", count: 28 },
  { icon: History, name: "Jain History", count: 24 },
  { icon: Library, name: "Tattvartha Sutra", count: 19 },
];

const featuredPublications = [
  {
    title: "The Doctrine of Karma in Jain Philosophy",
    author: "Dr. Prabhakaran Jain",
    journal: "Journal of Jain Studies",
    year: 2024,
    citations: 45,
  },
  {
    title: "Prakrit Manuscripts: A Digital Preservation Study",
    author: "Prof. Sadhvi M.S.",
    journal: "Indian Manuscript Studies",
    year: 2023,
    citations: 32,
  },
  {
    title: "Comparative Analysis of Jain and Buddhist Ethics",
    author: "Dr. Robert Zydenbos",
    journal: "Asian Philosophy Review",
    year: 2023,
    citations: 28,
  },
];

const getBadgeStyle = (badge: string) => {
  switch (badge) {
    case "gold":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "blue":
      return "bg-blue-100 text-blue-700 border-blue-200";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

const getBadgeLabel = (badge: string) => {
  switch (badge) {
    case "gold":
      return "Senior Scholar";
    case "blue":
      return "Active Scholar";
    default:
      return "New Scholar";
  }
};

const Scholars = () => {
  return (
    <Layout>
      <Seo
        title="Scholars & Researchers"
        description="Join our global community of Jain scholars dedicated to preserving and studying the teachings of Gurudev Muni Jambuvijayaji Maharaj Saheb."
      />
      {/* Enhanced Hero */}
      <section className="bg-gradient-hero lotus-pattern relative overflow-hidden py-16 lg:py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/50" />
        <div className="container relative mx-auto px-4">
          <AnimatedContainer
            animation="fade-up"
            className="mx-auto max-w-4xl text-center"
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm">
              <GraduationCap className="h-4 w-4 text-primary" />
              <span className="font-medium text-primary">Scholar Portal</span>
            </div>
            <h1 className="font-heading mb-6 text-4xl font-bold text-foreground sm:text-5xl lg:text-6xl">
              Scholars & Researchers
            </h1>
            <p className="font-body mx-auto mb-4 max-w-2xl text-lg text-muted-foreground">
              Join our global community of scholars dedicated to advancing Jain
              studies, manuscript research, and the preservation of ancient
              wisdom.
            </p>
            <p className="font-body mx-auto mb-8 max-w-xl text-muted-foreground">
              Connect with leading researchers, access rare manuscripts, and
              contribute to the scholarly understanding of Jain philosophy and
              literature.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                variant="hero"
                size="lg"
                asChild
                className="shadow-elevated"
              >
                <Link to="/scholar/login">
                  <LogIn className="mr-2 h-5 w-5" />
                  Scholar Portal
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/auth">
                  <GraduationCap className="mr-2 h-5 w-5" />
                  Join as Scholar
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/research">
                  <Search className="mr-2 h-5 w-5" />
                  Explore Research Tools
                </Link>
              </Button>
            </div>
          </AnimatedContainer>
        </div>
      </section>

      {/* Core Scholar Tools */}
      <section className="bg-secondary/30 py-14 lg:py-16">
        <div className="container mx-auto px-4">
          <AnimatedContainer animation="fade-up" className="mb-10 text-center">
            <h2 className="font-heading mb-2 text-2xl font-bold text-foreground lg:text-3xl">
              Core Scholar Tools
            </h2>
            <p className="font-body mx-auto max-w-lg text-muted-foreground">
              Everything you need to advance your research in Jain studies
            </p>
          </AnimatedContainer>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <AnimatedContainer
                key={feature.title}
                animation="fade-up"
                delay={index * 100}
              >
                <Link to={feature.href}>
                  <Card className="group h-full cursor-pointer overflow-hidden rounded-2xl border border-border/50 bg-card shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <CardContent className="flex flex-col items-center p-6 text-center">
                      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                        <feature.icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="font-heading mb-2 text-lg font-semibold text-foreground">
                        {feature.title}
                      </h3>
                      <p className="font-body mb-4 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                        {feature.description}
                      </p>
                      <span className="inline-flex items-center text-sm font-medium text-primary transition-all group-hover:gap-2">
                        {feature.cta}
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </AnimatedContainer>
            ))}
          </div>
        </div>
      </section>

      {/* Scholar Directory */}
      <section className="bg-background py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <AnimatedContainer animation="fade-up" className="mb-10 text-center">
            <h2 className="font-heading mb-4 text-3xl font-bold text-foreground lg:text-4xl">
              Featured Scholars
            </h2>
            <p className="font-body mx-auto max-w-xl text-muted-foreground">
              Distinguished members of our research community making significant
              contributions to Jain studies
            </p>
          </AnimatedContainer>

          {/* Smart Filters */}
          <AnimatedContainer animation="fade-up" delay={100}>
            <Card variant="flat" className="mb-10">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Search className="h-4 w-4" />
                    <span className="text-sm font-medium">Filter:</span>
                  </div>
                  <Select>
                    <SelectTrigger className="h-10 w-[160px] bg-background">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agama">Agama</SelectItem>
                      <SelectItem value="philosophy">Philosophy</SelectItem>
                      <SelectItem value="manuscriptology">
                        Manuscriptology
                      </SelectItem>
                      <SelectItem value="linguistics">Linguistics</SelectItem>
                      <SelectItem value="history">History</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select>
                    <SelectTrigger className="h-10 w-[140px] bg-background">
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="india">India</SelectItem>
                      <SelectItem value="germany">Germany</SelectItem>
                      <SelectItem value="uk">UK</SelectItem>
                      <SelectItem value="usa">USA</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select>
                    <SelectTrigger className="h-10 w-[160px] bg-background">
                      <SelectValue placeholder="Affiliation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="university">University</SelectItem>
                      <SelectItem value="research-institute">
                        Research Institute
                      </SelectItem>
                      <SelectItem value="independent">Independent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select>
                    <SelectTrigger className="h-10 w-[180px] bg-background">
                      <SelectValue placeholder="Sort By" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Most Active</SelectItem>
                      <SelectItem value="recent">Recently Updated</SelectItem>
                      <SelectItem value="senior">Senior Scholars</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </AnimatedContainer>

          {/* Scholar Cards with Photos */}
          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {scholars.map((scholar, index) => (
              <AnimatedContainer
                key={scholar.id}
                animation="fade-up"
                delay={index * 100}
              >
                <Card
                  variant="feature"
                  className="hover:shadow-elevated group h-full transition-all duration-300 hover:-translate-y-1"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className="relative shrink-0">
                        <img
                          src={scholar.avatar}
                          alt={scholar.name}
                          className="h-16 w-16 rounded-full border-2 border-primary/20 object-cover transition-colors group-hover:border-primary/40"
                        />
                        <div
                          className={`absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full ${
                            scholar.badge === "gold"
                              ? "bg-amber-500"
                              : scholar.badge === "blue"
                                ? "bg-blue-500"
                                : "bg-muted-foreground"
                          }`}
                        >
                          <Star className="h-3 w-3 text-white" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-start justify-between gap-2">
                          <CardTitle className="font-heading truncate text-base transition-colors group-hover:text-primary">
                            {scholar.name}
                          </CardTitle>
                        </div>
                        <p className="font-body truncate text-sm text-muted-foreground">
                          {scholar.affiliation}
                        </p>
                        <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {scholar.country}
                        </div>
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`mt-2 w-fit text-xs ${getBadgeStyle(scholar.badge)}`}
                    >
                      {getBadgeLabel(scholar.badge)}
                    </Badge>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="mb-4 flex flex-wrap gap-1.5">
                      {scholar.specialization.map(spec => (
                        <Badge
                          key={spec}
                          variant="secondary"
                          className="text-xs"
                        >
                          {spec}
                        </Badge>
                      ))}
                    </div>

                    {/* Stats Grid */}
                    <div className="mb-3 grid grid-cols-3 gap-2 border-b border-t border-border py-3">
                      <div className="text-center">
                        <p className="font-heading font-bold text-foreground">
                          {scholar.publications}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Publications
                        </p>
                      </div>
                      <div className="border-x border-border text-center">
                        <p className="font-heading font-bold text-foreground">
                          {scholar.citations}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Citations
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="font-heading font-bold text-foreground">
                          {scholar.manuscripts}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Manuscripts
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">
                        {scholar.contributions} entries
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs group-hover:text-primary"
                      >
                        View Profile
                        <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedContainer>
            ))}
          </div>

          <AnimatedContainer
            animation="fade-up"
            delay={600}
            className="mt-10 text-center"
          >
            <Button variant="outline" size="lg" asChild>
              <Link to="/scholars/directory">
                View All Scholars
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </AnimatedContainer>
        </div>
      </section>

      {/* Scholar Highlights Stats Row */}
      <section className="bg-gradient-spiritual border-y border-border py-16">
        <div className="container mx-auto px-4">
          <AnimatedContainer animation="fade-up" className="mb-10 text-center">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground lg:text-3xl">
              Scholar Highlights
            </h2>
            <p className="font-body text-muted-foreground">
              Impact and reach of our global research community
            </p>
          </AnimatedContainer>
          <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 md:grid-cols-4">
            {highlights.map((item, index) => (
              <AnimatedContainer
                key={item.title}
                animation="scale-in"
                delay={index * 100}
              >
                <Card
                  variant="glass"
                  className="hover:shadow-elevated h-full text-center transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="pb-5 pt-6">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 shadow-sm">
                      <item.icon className="h-7 w-7 text-primary" />
                    </div>
                    <p className="font-heading mb-1 text-3xl font-bold text-primary lg:text-4xl">
                      {item.value}
                    </p>
                    <h3 className="font-heading mb-1 font-semibold text-foreground">
                      {item.title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </AnimatedContainer>
            ))}
          </div>
        </div>
      </section>

      {/* Why Become a Scholar - Cards */}
      <section className="bg-background py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <AnimatedContainer animation="fade-up" className="mb-12 text-center">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground lg:text-3xl">
              Why Become a Scholar?
            </h2>
            <p className="font-body mx-auto max-w-lg text-muted-foreground">
              Join our network and unlock exclusive benefits for your research
              journey
            </p>
          </AnimatedContainer>
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-3">
            {benefits.map((benefit, index) => (
              <AnimatedContainer
                key={benefit.title}
                animation="fade-up"
                delay={index * 150}
              >
                <Card
                  variant="feature"
                  className="hover:shadow-elevated h-full text-center transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="pb-6 pt-8">
                    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 shadow-sm">
                      <benefit.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-heading mb-3 text-lg font-semibold text-foreground">
                      {benefit.title}
                    </h3>
                    <p className="font-body text-muted-foreground">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </AnimatedContainer>
            ))}
          </div>
        </div>
      </section>

      {/* Top Research Domains Grid */}
      <section className="border-y border-border bg-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <AnimatedContainer animation="fade-up" className="mb-10 text-center">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground lg:text-3xl">
              Top Research Domains
            </h2>
            <p className="font-body text-muted-foreground">
              Explore the key areas of Jain scholarship and research
            </p>
          </AnimatedContainer>
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {researchDomains.map((domain, index) => (
              <AnimatedContainer
                key={domain.name}
                animation="scale-in"
                delay={index * 75}
              >
                <Card
                  variant="interactive"
                  className="group cursor-pointer text-center"
                >
                  <CardContent className="px-3 py-5">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                      <domain.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-heading mb-1 text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                      {domain.name}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {domain.count} publications
                    </p>
                  </CardContent>
                </Card>
              </AnimatedContainer>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Publications Section */}
      <section className="bg-background py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <AnimatedContainer animation="fade-up" className="mb-10 text-center">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground lg:text-3xl">
              Featured Publications
            </h2>
            <p className="font-body mx-auto max-w-lg text-muted-foreground">
              Recent notable research contributions from our scholarly community
            </p>
          </AnimatedContainer>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            {featuredPublications.map((pub, index) => (
              <AnimatedContainer
                key={pub.title}
                animation="fade-up"
                delay={index * 100}
              >
                <Card
                  variant="feature"
                  className="hover:shadow-elevated group h-full transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="pt-6">
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">
                          {pub.journal}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {pub.year}
                        </p>
                      </div>
                    </div>
                    <h3 className="font-heading mb-2 line-clamp-2 font-semibold text-foreground transition-colors group-hover:text-primary">
                      {pub.title}
                    </h3>
                    <p className="mb-3 text-sm text-muted-foreground">
                      {pub.author}
                    </p>
                    <div className="flex items-center justify-between border-t border-border pt-3">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <TrendingUp className="h-4 w-4" />
                        <span>{pub.citations} citations</span>
                      </div>
                      <Button variant="ghost" size="sm" className="text-xs">
                        <ExternalLink className="mr-1 h-3 w-3" />
                        Read
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedContainer>
            ))}
          </div>
        </div>
      </section>

      {/* Upgraded Become a Scholar CTA */}
      <section className="bg-gradient-spiritual border-t border-border py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <AnimatedContainer animation="fade-up">
              <Card variant="elevated" className="overflow-hidden">
                <CardContent className="relative p-8 text-center lg:p-12">
                  <div className="absolute left-0 right-0 top-0 h-1 bg-gradient-to-r from-primary via-gold to-primary" />

                  <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                    <GraduationCap className="h-8 w-8 text-primary" />
                  </div>

                  <h2 className="font-heading mb-4 text-2xl font-bold text-foreground lg:text-3xl">
                    Become a Contributing Scholar
                  </h2>

                  <p className="font-body mx-auto mb-6 max-w-lg text-muted-foreground">
                    Apply to join our scholarly network. Get access to research
                    tools, contribute your work, and collaborate with fellow
                    researchers worldwide.
                  </p>

                  {/* Quote */}
                  <div className="mx-auto mb-8 max-w-md rounded-xl bg-secondary/50 p-5">
                    <Quote className="mx-auto mb-2 h-6 w-6 text-primary/40" />
                    <p className="font-body text-sm italic text-muted-foreground">
                      "Knowledge shared is knowledge multiplied."
                    </p>
                  </div>

                  <div className="flex flex-wrap justify-center gap-4">
                    <Button
                      variant="hero"
                      size="lg"
                      asChild
                      className="shadow-elevated"
                    >
                      <Link to="/auth">
                        Apply Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/contact">
                        <Mail className="mr-2 h-5 w-5" />
                        Contact Us
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </AnimatedContainer>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="border-t border-border bg-background py-14">
        <div className="container mx-auto px-4">
          <AnimatedContainer
            animation="fade-in"
            className="mx-auto max-w-2xl text-center"
          >
            <div className="mx-auto mb-6 h-px w-16 bg-primary/30" />
            <Quote className="mx-auto mb-4 h-10 w-10 text-primary/20" />
            <blockquote className="font-body mb-4 text-xl italic text-muted-foreground">
              "The pursuit of knowledge is the path to liberation."
            </blockquote>
            <p className="font-heading text-sm font-semibold text-foreground">
              — Gurudev Muni Jambuvijayaji Maharaj Saheb
            </p>
            <div className="mx-auto mt-6 h-px w-16 bg-primary/30" />
          </AnimatedContainer>
        </div>
      </section>
    </Layout>
  );
};

export default Scholars;
